import type { BikeState } from './bike';
import type { CenterLine, LinePoint } from './centerline';
import { InputFrame } from './input';
import { clamp } from './math';

/**
 * Simplified control mode: strong assists layered on the same physics (there is no second physics engine).
 *
 * The player gives an intent (throttle, one brake, steer); `assistInput` turns it, with what the bike is doing, into
 * the levers a good rider would work: body position, stance, the front/rear brake split with ABS-like release,
 * traction control, a corner speed governor and gentle levers in the air. The rest of the help lives in the physics
 * step itself, gated on `InputFrame.assist` (src/sim/bike.ts: a stronger balance controller with no low-speed
 * tip-over, the wheelie capped near 15 deg, air pitch levelling toward a parallel landing, and much more forgiving
 * crash rules). Real mode never runs any of this.
 *
 * Deterministic and allocation-free: the state is a plain object owned by the world, the output a reused frame.
 */
/** updateLimit's search result (reused). */
const FOUND: LinePoint = { s: 0, d: 0, index: 0 };

export interface AssistState {
  abs: [number, number]; // brake release factor per wheel [rear, front], 1 = full lever
  tc: number;            // traction control throttle factor
  body: number;          // body position the assist holds
  standing: boolean;
  limit: number;         // m/s the track ahead allows (updateLimit), Infinity where there is no known line
  s: number;             // arc length of the bike on the level's line (NaN: not found yet)
  trick: number;         // TRICK.none / wheelie / stoppie: the lean trick the assist is riding (see assistTrick)
  pitchPrev: number;     // the bike's pitch a step ago (the trick controllers' rate term)
  target: number;        // rad the wheelie controller holds now (ramps down for a soft landing)
}

/** Lean tricks of the default (assisted) scheme: what `AssistState.trick` and `InputFrame.trick` hold. */
export const TRICK = { none: 0, wheelie: 1, stoppie: 2 } as const;

export const createAssist = (): AssistState => ({ abs: [1, 1], tc: 1, body: 0, standing: false, limit: Infinity, s: NaN, trick: 0, pitchPrev: 0, target: 0 });

export function resetAssist(a: AssistState): void {
  a.abs[0] = 1; a.abs[1] = 1; a.tc = 1; a.body = 0; a.standing = false; a.limit = Infinity; a.s = NaN;
  a.trick = 0; a.pitchPrev = 0; a.target = 0;
}

/** Tuning of the simplified mode (not per bike: every fleet bike gets the same help). */
export const ASSIST = {
  lockSlip: -0.12,     // slip ratio past which a braked wheel is released (ABS)
  spinSlip: 1.0,       // rear slip / peak slip past which traction control cuts the throttle
  rearScale: 0.68,     // x the rear lever the assist applies (260 of the rear brake's 380 N*m)
  frontShare: 0.6,     // share of the one brake that goes to the front, upright
  frontShareLean: 0.6, // rad of lean where the front share has gone to nothing (no front washout in a turn)
  airLever: 0.35,      // throttle and brake scale in the air (still work, gently)
  governorSteer: 0.7,  // |steer| past which the asked corner is tighter than the speed allows: ease off
  governorSpeed: 5,    // m/s below which the governor leaves the throttle alone
  standSpeed: 4,       // m/s above which the rider stands (rough ground, jumps) unless turning
  seatSteer: 0.3,      // |steer| past which the rider sits for the turn
  bodyPitch: 2.2,      // body forward per rad of nose-up (climb), back on a descent
  bodyThrottle: 0.5,   // body forward on the gas at low speed
  frontLight: 250,     // N of front load under which the throttle eases (anti-wheelie)
  bodyBrake: 0.5,      // body back under braking
  // Physics side (src/sim/bike.ts).
  cornerAccel: 7,      // m/s^2 of cornering the track speed limit plans for (a dirt bike in a berm holds ~0.7 g)
  planDecel: 3,        // m/s^2 of braking it plans with
  lineReach: 15,       // m off the centreline where the speed management lets go
  minLimit: 5,         // m/s it never asks to go below
  topSpeed: 75 / 3.6,  // m/s Simplified holds at most (Real keeps the bikes' 90-120 km/h), owner 2026-10-05/06
  lateralAccel: 8.5,   // m/s^2 full steer asks for at speed (real mode: steerLateralAccel)
  maxLean: 0.75,       // rad the balance controller leans to at most
  leanGuardKp: 20000,  // N*m/rad pushing back past maxLean + 0.08 (no tip-over in a turn)
  leanGuardKd: 1500,   // N*m*s/rad of roll damping while past the limit
  governorLean: [0.4, 0.6] as readonly [number, number], // rad of lean where the throttle eases off .. is shut
  balanceGain: 3,      // x the rider's balance torque, full from walking pace (no low-speed tip-over)
  uprightTorque: 900,  // N*m cap of the stand-still hold (a foot always down in slow turns)
  airRollKp: 3000,     // N*m/rad levelling the roll in the air
  airRollKd: 500,
  airYawKp: 600,       // N*m/rad straightening a whip once the input is let go
  // Lean tricks (assistTrick). The physics stays the same; the assist only works the levers like an expert would.
  trickLean: 0.5,      // |lean| (InputFrame.body) that asks for a trick
  trickSteer: 0.3,     // |steer| under which the bike counts as going straight
  wheelieTarget: 0.68, // rad (39 deg): the balance point the wheelie is held at
  wheelieMaxSpeed: 14, // m/s above which lean back + gas is just weight back (no wheelie)
  wheelieBand: [0.06, 0.26] as readonly [number, number], // rad: roll-off starts / full (a wheelie tops out near 15 deg)
  trickBand: [0.85, 1.05] as readonly [number, number],   // rad: the physics' last-resort roll-off while a wheelie is asked
  landRate: 0.45,      // rad/s the wheelie target comes down at after the lean is let go (a soft landing)
  stoppieTarget: 0.2,  // rad (11 deg) of tail up the stoppie is held at
  stoppieMinSpeed: 7,  // m/s (25 km/h) needed to start a stoppie
  stoppieEndSpeed: 2.5,// m/s under which the stoppie is put down
  airLean: 0.8,        // lever (throttle back, rear brake forward) a full lean asks for in the air
} as const;

/**
 * Raw player intent + bike -> the input the physics steps with (written into `out`, which keeps `assist` set).
 * Reads the bike as it was after the previous step, as a rider feels it.
 */
export function assistInput(a: AssistState, raw: InputFrame, bike: BikeState, out: InputFrame): void {
  const tel = bike.telemetry;
  const speed = tel.speed;
  const air = tel.airborne;
  const steer = clamp(raw.steer, -1, 1);
  const lever = Math.max(clamp(raw.brake, 0, 1), clamp(raw.frontBrake ?? 0, 0, 1), clamp(raw.rearBrake ?? 0, 0, 1));
  let throttle = clamp(raw.throttle, 0, 1);
  let brake = lever;
  if (assistTrick(a, raw, bike, out)) return;

  // Speed management on a known track (updateLimit): off the gas above what the next corner or jump allows, and
  // brakes if well over it. A held W arrives at every corner and lip at a speed it can take.
  const limit = Math.min(a.limit, ASSIST.topSpeed);
  if (!air && speed > limit) {
    throttle = 0;
    brake = Math.max(brake, clamp((speed - limit - 0.5) * 0.25, 0, 0.7));
  }

  // Corner governor: steer held near full at speed means the asked line is tighter than the speed allows (the sim
  // caps the curvature by lateral acceleration), so the rider eases the gas instead of driving into it. It no longer
  // brakes: that took a full-steer bike from 50 to 13 km/h and every corner became a crawl (owner 2026-10-06, point
  // 1); the bike runs the capped radius at speed, and the slide takes care of the rest.
  const tight = clamp((Math.abs(steer) - ASSIST.governorSteer) / (1 - ASSIST.governorSteer), 0, 1);
  if (!air && tight > 0 && speed > ASSIST.governorSpeed) throttle *= 1 - 0.5 * tight;

  // Leaned far over: the throttle eases off (the bike would run wide or lie down).
  const gl = ASSIST.governorLean;
  if (!air) throttle *= clamp((gl[1] - Math.abs(tel.roll)) / (gl[1] - gl[0]), 0, 1);

  // Anti-wheelie: the front going light under power eases the throttle before it lifts (the physics also caps a
  // wheelie that does happen at ~15 deg).
  const frontW = bike.wheels[1];
  if (!air && bike.wheels[0].contact) throttle *= clamp(frontW.load / ASSIST.frontLight, 0.15, 1);

  // Traction control on the rear slip the last step had.
  a.tc = tel.rearSlip > ASSIST.spinSlip ? Math.max(0.2, a.tc - 0.08) : Math.min(1, a.tc + 0.02);
  throttle *= a.tc;

  // One brake, split by lean, each wheel released when it starts to lock or to slide sideways (ABS on the grip circle:
  // no rear stepping out under braking in a turn).
  const rear = bike.wheels[0], front = bike.wheels[1];
  a.abs[0] = rear.contact && (rear.slipRatio < ASSIST.lockSlip || rear.usage > 1) && Math.abs(speed) > 1 ? Math.max(0.2, a.abs[0] - 0.15) : Math.min(1, a.abs[0] + 0.05);
  a.abs[1] = front.contact && (front.slipRatio < ASSIST.lockSlip || front.usage > 1) && Math.abs(speed) > 1 ? 0 : Math.min(1, a.abs[1] + 0.05); // a sliding front dumps its pressure at once
  const share = ASSIST.frontShare * clamp(1 - Math.abs(tel.roll) / ASSIST.frontShareLean, 0, 1);
  let frontLever = brake * share * a.abs[1];
  // (rearScale: the assist brakes the rear as hard as it did before the rear brake was made stronger for Real's slides;
  // at the full new torque its corner braking locked the rear and swung the bike 45 deg, owner 2026-10-05.)
  let rearLever = brake * (1 - 0.5 * share) * a.abs[0] * ASSIST.rearScale;

  if (air) {
    throttle *= ASSIST.airLever; frontLever = 0; rearLever *= ASSIST.airLever;
    // Lean in the air pitches the bike through the wheels' momentum (the physics' own rule): back = the gas (nose
    // up), forward = the rear brake (nose down).
    const lean = clamp(raw.body, -1, 1);
    if (lean < -0.1) throttle = Math.max(throttle, -lean * ASSIST.airLean);
    if (lean > 0.1) { rearLever = Math.max(rearLever, lean * ASSIST.airLean); throttle = 0; }
  }
  else if (brake > 0.05) throttle = 0;

  // Body: forward on a climb and on the gas, back on a descent and under braking; held still in the air (a body
  // move there would pitch the bike).
  if (!air) {
    const want = tel.pitch * ASSIST.bodyPitch + (throttle > 0.3 ? ASSIST.bodyThrottle : 0) - brake * ASSIST.bodyBrake;
    a.body = clamp(want, -1, 1);
  }
  // Stance: standing on the straights, over rough ground and in the air; seated for a turn and at walking pace.
  a.standing = air || (Math.abs(steer) < ASSIST.seatSteer && Math.abs(speed) > ASSIST.standSpeed);

  out.throttle = throttle;
  out.brake = 0;
  out.frontBrake = clamp(frontLever, 0, 1);
  out.rearBrake = clamp(rearLever, 0, 1);
  out.steer = steer;
  out.body = a.body;
  // With the automatic posture the rider picks his stance in the physics (a held Space flips it); the assist's own
  // stance rule is for callers that set the stance themselves.
  out.standing = raw.autoPosture ? raw.standing : a.standing;
  out.autoPosture = raw.autoPosture;
  out.foot = false;
  out.autoFoot = true;
  out.assist = true;
  out.trick = TRICK.none;
}

/**
 * The lean tricks (owner decision B, GTA style): lean back + gas going straight is an assisted wheelie, lean forward +
 * brake going straight at speed an assisted stoppie. The assist works the real levers (throttle and rear brake hold
 * the wheelie's balance point, the front brake holds the tail up with the bars straight); the physics is the same,
 * so a wheelie into a turn or over bumps can still go wrong. Letting go of the lean lowers the wheel softly.
 * Returns true when it wrote `out` (a trick is running).
 */
function assistTrick(a: AssistState, raw: InputFrame, bike: BikeState, out: InputFrame): boolean {
  const tel = bike.telemetry;
  const pitch = tel.pitch, rate = (pitch - a.pitchPrev) * 240; // rad/s (the sim steps at 240 Hz)
  a.pitchPrev = pitch;
  const lean = clamp(raw.body, -1, 1), steer = clamp(raw.steer, -1, 1);
  const rear = bike.wheels[0], front = bike.wheels[1];
  const straight = Math.abs(steer) < ASSIST.trickSteer;
  const speed = tel.speed;
  if (tel.airborne || tel.fallen) { a.trick = TRICK.none; return false; }

  if (a.trick === TRICK.wheelie) {
    if (!front.contact || pitch > 0.05) {
      // Held: the target is the balance point; let go (or a turn asked): it comes down at landRate.
      const held = lean < -ASSIST.trickLean && raw.throttle > 0.2 && straight;
      a.target = held ? ASSIST.wheelieTarget : Math.max(-0.1, Math.min(a.target, pitch) - ASSIST.landRate / 240);
    } else a.trick = TRICK.none; // both wheels down: done
  } else if (a.trick === TRICK.stoppie) {
    // (Bars turned hard ends the assisted stoppie: the raw input then rides it, and a turned front on its own is unstable.)
    if (!(lean > ASSIST.trickLean && lever(raw) > 0.2 && speed > ASSIST.stoppieEndSpeed && straight)) a.trick = TRICK.none;
  }
  if (a.trick === TRICK.none && rear.contact && front.contact && straight) {
    if (lean < -ASSIST.trickLean && raw.throttle > 0.2 && speed > 0.5 && speed < ASSIST.wheelieMaxSpeed) { a.trick = TRICK.wheelie; a.target = ASSIST.wheelieTarget; }
    else if (lean > ASSIST.trickLean && lever(raw) > 0.2 && speed > ASSIST.stoppieMinSpeed) a.trick = TRICK.stoppie;
  }
  if (a.trick === TRICK.none) return false;

  out.steer = steer;
  out.brake = 0;
  out.foot = false;
  out.autoFoot = true;
  out.assist = true;
  out.trick = a.trick;
  out.autoPosture = raw.autoPosture;
  out.standing = raw.autoPosture ? raw.standing : false;
  if (a.trick === TRICK.wheelie) {
    // Throttle for the balance point, the rear brake if it goes past it (an expert's covering finger).
    const e = a.target - pitch;
    out.throttle = clamp(0.35 + 3.5 * e - 0.6 * rate, 0, 1);
    out.rearBrake = clamp((-e - 0.03) * 5 + (rate > 0.6 && e < 0 ? 0.3 : 0), 0, 1);
    out.frontBrake = 0;
    out.body = -1;
    a.body = -1;
  } else {
    // Stoppie: bars straight, the front lever eased to hold the tail near stoppieTarget, weight forward.
    const lift = -pitch, e = ASSIST.stoppieTarget - lift; // > 0: the tail is not up enough
    out.steer = 0;
    out.throttle = 0;
    out.rearBrake = 0;
    out.frontBrake = clamp((front.contact && !rear.contact ? 0.6 : 0.9) + 3 * e - 0.5 * -rate, 0, 1) * clamp(lever(raw) * 1.5, 0, 1);
    out.body = 1;
    a.body = 1;
  }
  return true;
}

const lever = (raw: InputFrame) => Math.max(clamp(raw.brake, 0, 1), clamp(raw.frontBrake ?? 0, 0, 1), clamp(raw.rearBrake ?? 0, 0, 1));

/**
 * The speed the track ahead allows, from the level's centreline: corners by ASSIST.cornerAccel, jumps at their
 * reference speed, reached with ASSIST.planDecel of braking. Run a few times a second (it walks the line ahead).
 * Off the line (further than ASSIST.lineReach) there is no limit.
 */
export function updateLimit(a: AssistState, line: CenterLine | null, jumpLimit: ((s: number) => number) | null, x: number, z: number, speed: number): void {
  if (!line) { a.limit = Infinity; return; }
  const c = FOUND;
  if (Number.isNaN(a.s)) { const f = line.closest(x, z); c.s = f.s; c.d = f.d; c.index = f.index; } // first time: the whole line
  else line.closestInto(x, z, a.s, 30, c);
  // (The found spot is kept as the next search's hint even off the line: a search without one scans the whole line.)
  a.s = c.s;
  if (Math.abs(c.d) > ASSIST.lineReach) { a.limit = Infinity; return; }
  let target = Infinity;
  const horizon = (speed * speed) / (2 * ASSIST.planDecel) + 15;
  for (let d = 0; d <= horizon; d += 2) {
    const k = Math.abs(line.curvatureAtInto(c.s + d));
    let lim = k > 1e-4 ? Math.sqrt(ASSIST.cornerAccel / k) : Infinity;
    if (jumpLimit) lim = Math.min(lim, jumpLimit(c.s + d));
    target = Math.min(target, Math.sqrt(lim * lim + 2 * ASSIST.planDecel * d));
  }
  a.limit = Math.max(ASSIST.minLimit, target);
}
